import { isEmpty } from "lodash";
import React, { useEffect, useRef } from "react";
import { useHandleUserInteractionLogger } from "../../hooks/useHandleUserInteractionLogger";
import type { TrackSharedProps } from "./Track.props";

export default function Track({ children }: TrackProps): React.ReactNode {
  return children;
}

Track.Click = function Click({
  analytics,
  enabled = true,
  children,
}: TrackProps) {
  const { trackButtonClick } = useHandleUserInteractionLogger();

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (enabled) {
      trackButtonClick(event, analytics);
    }
  };
  return React.cloneElement(children as React.ReactElement, {
    onClickCapture: handleClick,
  });
};

Track.Impression = function Impression({
  analytics,
  trackOnce = true,
  children,
}: TrackProps & {
  /**
   * Whether we should only track this impression once
   */
  trackOnce?: boolean;
}) {
  /**
   * This component is used to track impressions of elements when they become visible in the viewport.
   * It wraps the children components and fires the provided analytics function when the wrapped element
   * becomes at least 50% visible(threshold: 0.5) in the viewport.
   * Usage:
   * Wrap the element you want to track impressions for with <Track.Impression> component.
   * Provide the analytics data in props that will be passed to analytics endpoint when the element becomes visible.
   *
   * Example:
   * <Track.Impression analytics={label:"test"}>
   *   <YourComponentToTrackImpression />
   * </Track.Impression>
   *
   * If `trackOnce` is true, the component fires the analytics function only once for the first time the wrapped element becomes visible
   * in the viewport.
   *
   * If `trackOnce` is false, the component fires the analytics function once each time the wrapped element becomes visible
   * in the viewport. It ensures that the analytics function is only called once per element
   * even if the element becomes visible multiple times due to scrolling.
   */
  const { trackImpression } = useHandleUserInteractionLogger();
  const childRef = useRef<HTMLElement | null>(null);
  const options = {
    threshold: 0.5,
  };

  const trackOnceRef = useRef(trackOnce);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (childRef.current) {
          observer.unobserve(childRef.current);
        }
        if (!isEmpty(analytics)) {
          // If the `trackOnce` flag is true, then make sure the element intersction is only tracked the first time.
          if (
            !trackOnceRef.current ||
            (trackOnceRef.current && !hasTrackedRef.current)
          ) {
            trackImpression(analytics);
          }

          // If `trackOnce` flag is true, then update our `hasTracked` control variable.
          if (trackOnceRef.current) {
            hasTrackedRef.current = true;
          }
        }
      }
    }, options);
    if (childRef.current) {
      observer.observe(childRef.current);
    }
    return () => {
      if (childRef.current) {
        observer.unobserve(childRef.current);
      }
    };
  }, [childRef.current]);

  return (
    <>
      {React.Children.map(children, (child) => {
        return React.cloneElement(child as React.ReactElement, {
          ref: (ref: HTMLElement) => {
            childRef.current = ref;
          },
        });
      })}
    </>
  );
};

export type TrackProps = TrackSharedProps;
